import _get from 'lodash/get'
import { baseRequest } from '.'

import { eventAdapter, scheduledMessagesAdapter, scheduledInvitationsAdapter } from './adapters'
import { fetchAPI } from '../http'

export const getEvents = async (offset, limit, total) => {
  const params = {
    offset,
    limit,
    compact: 'event-list',
  }
  //IF offset = 0 then dont send total, otherwise send total to avoid extra calculations in the backend
  if (offset !== 0) {
    params.total = total
  }
  const res = await baseRequest.get(`events/`, {
    params,
  })
  return _get(res, 'data')
}

export const getFiveEvents = async () => {
  const params = {
    offset: 0,
    limit: 5,
    total: 5,
    compact: 'event-list',
    section_type: 'next_events',
    order: 'ASC',
  }

  const res = await baseRequest.get(`events/`, {
    params,
  })
  return _get(res, 'data')
}

export const pinEvents = async (eventId, pinned = false) => {
  const res = await baseRequest[pinned ? 'delete' : 'post'](`/event/pin/${eventId}/`)
  return _get(res, 'data')
}

export const getDraftEvents = async (offset, limit) => {
  const res = await baseRequest.get(`draft-events/`, {
    params: {
      offset,
      limit,
    },
  })

  const data = _get(res, 'data.data') || []
  return { data }
}

export const updateEventStatus = async (eventId, status, data = {}) => {
  const { flagSendPromoterLinks = false, externalSupportEmail, internalSupportEmail } = data
  return await baseRequest.patch(`event/${eventId}/status/`, {
    data: {
      attributes: {
        ...data,
        status,
        send_promoter_links: flagSendPromoterLinks,
        externalSupportEmail,
        internalSupportEmail,
      },
    },
  })
}

export const getEvent = async eid => {
  const res = await baseRequest.get(`events/${eid}/`)
  const data = _get(res, 'data.data')
  return eventAdapter(data)
}

export const fetchEventSupport = async eventId => {
  const res = await baseRequest.get(`event/${eventId}/support/`)

  return _get(res, 'data.data')
}
export const fetchEventTraffic = async (eventId, params) => {
  const res = await baseRequest.get(`events/${eventId}/relationships/page-views`, {
    params,
  })
  return _get(res, 'data.data')
}

export const fetchEventTrafficTable = async (eventId, params) => {
  const res = await baseRequest.get(`events/${eventId}/relationships/page-views-table`, {
    params,
  })
  return _get(res, 'data.data')
}

export const fetchEventTrafficSubtable = async (eventId, params) => {
  const res = await baseRequest.get(`events/${eventId}/relationships/page-views-subtable`, {
    params,
  })
  return _get(res, 'data.data')
}

export const fetchEventTrafficOriginsByDay = async (eventId, params) => {
  const res = await baseRequest.get(`events/${eventId}/relationships/referrer-url-by-day`, { params })
  return _get(res, 'data.data')
}

export const updateEventSupport = async (eventId, values) => {
  const res = await baseRequest.patch(`event/${eventId}/support/`, {
    data: {
      attributes: {
        ...values,
      },
    },
  })

  return res
}
// The bot-protection token rides in the same attributes bag the backend's
// middleware reads. Absent, the request is allowed and recorded as unverified.
export const createEvent = async (data, botProtectionToken) => {
  const res = await baseRequest.post(`events/`, {
    data: {
      ...data,
      type: 'event',
      ...(botProtectionToken ? { attributes: { ...(data.attributes || {}), botProtectionToken } } : {}),
    },
  })
  const event = _get(res, 'data.data')
  return eventAdapter(event)
}

export const duplicateEvent = async (eId, data) => {
  const res = await baseRequest.post(`event/${eId}/clone`, {
    data: {
      ...data,
      type: 'event',
    },
  })
  const event = _get(res, 'data.data')
  return eventAdapter(event)
}

export const updateEvent = async (eid, data) => {
  const res = await baseRequest.patch(`events/${eid}/`, {
    data: {
      ...data,
      id: eid,
      type: 'event',
    },
  })
  const event = _get(res, 'data.data')
  return eventAdapter(event)
}

export const deleteEvent = async eid => {
  const res = await baseRequest.delete(`events/${eid}/`)
  const data = _get(res, 'data.data')
  return eventAdapter(data)
}

export const getInfluencersData = async eid => {
  const res = await baseRequest.get(`events/${eid}/relationships/referrals/`)
  return _get(res, 'data.data')
}

export const announceLocation = async (eid, location, subject) => {
  const res = await baseRequest.post(`event/${eid}/announce-location/`, {
    data: {
      attributes: {
        location,
        subject,
      },
    },
  })
  return res
}

// Internal Notes
export const fetchSupportNotes = async eventId => {
  const res = await baseRequest.get(`event/${eventId}/notes`)

  return _get(res, 'data.data')
}

export const createSupportNotes = async (eventId, payload) => {
  const res = await baseRequest.post(`event/${eventId}/note`, payload)

  return _get(res, 'data.data')
}

export const editSupportNotes = async (eventId, noteId, payload) => {
  const res = await baseRequest.put(`event/${eventId}/note/${noteId}`, payload)

  return _get(res, 'data.data')
}

export const deleteSupportNotes = async (eventId, noteId) => {
  const res = await baseRequest.delete(`event/${eventId}/note/${noteId}`)

  return _get(res, 'data.data')
}

// Custom Fields
export const createCustomFields = (eventId, payload) => baseRequest.post(`event/${eventId}/fields`, payload)

export const getCustomFields = eventId => baseRequest.get(`event/${eventId}/fields`)

export const deleteCustomFields = eventId => baseRequest.delete(`event/${eventId}/fields`)

export const getSheduledMessages = async eventId => {
  const res = await fetchAPI(`api/events/${eventId}/relationships/schedule/mailouts`)
  const adaptedResult = scheduledMessagesAdapter(res)
  return adaptedResult
}

export const getSheduledInvitations = async eventId => {
  const res = await fetchAPI(`api/events/${eventId}/relationships/scheduled/invitations`)
  const adaptedResult = scheduledInvitationsAdapter(res)
  return adaptedResult
}

export const deleteSheduledMessage = id =>
  fetchAPI(`api/schedule/message/${id}`, {
    method: 'DELETE',
  })

export const deleteSheduledInvitation = id =>
  fetchAPI(`api/scheduled/invitation/${id}`, {
    method: 'DELETE',
  })

export const addEventAdditionalCurrency = async (eventId, data) => {
  const res = await baseRequest.post(`events/${eventId}/additional-currencies`, data)
  return _get(res, 'data.data')
}
